import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Eye, Heart, Shield, Award, Globe, ArrowRight } from 'lucide-react';

export default function AboutPage() {
  const stats = [
    { num: '10,000+', label: 'Happy Travelers' },
    { num: '50+', label: 'Destinations' },
    { num: '4.9/5', label: 'Customer Rating' },
    { num: '10+', label: 'Years of Trust' },
  ];

  const values = [
    { icon: <Heart className="w-6 h-6" />, title: 'Passion for Travel', desc: 'We live and breathe travel. Our passion drives us to find the most incredible experiences for you.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Trust & Transparency', desc: 'No hidden charges, no surprises. We believe in honest pricing and transparent communication.' },
    { icon: <Users className="w-6 h-6" />, title: 'Customer First', desc: 'Your satisfaction is our priority. We go above and beyond to ensure every trip is perfect.' },
    { icon: <Globe className="w-6 h-6" />, title: 'Global Network', desc: 'Our partnerships with hotels, airlines, and local guides worldwide ensure the best experiences.' },
    { icon: <Award className="w-6 h-6" />, title: 'Quality Assured', desc: 'Every package is tested and verified. We only recommend places we truly believe in.' },
    { icon: <Eye className="w-6 h-6" />, title: 'Local Expertise', desc: 'Our team of travel experts has firsthand experience with every destination we offer.' },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1561501900-3701fa6a0864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBwb29sJTIwc3Vuc2V0fGVufDB8fHx8MTc3NDYwMjYxOHww&ixlib=rb-4.1.0&q=85"
          alt="About Beyond Horizons" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Our Story</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">About Beyond Horizons</h1>
          <p className="font-body text-base text-white/80 max-w-2xl">We don't just plan trips. We craft experiences that become stories you'll tell forever.</p>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-6">Where It All Began</h2>
        <p className="font-body text-base text-gray-600 leading-relaxed mb-6">
          Beyond Horizons was born from a simple belief — that travel should be more than just visiting places. It should be about creating moments that change you. Founded in Raipur, we started as a small team of passionate travelers who wanted to share the world's beauty with everyone.
        </p>
        <p className="font-body text-base text-gray-600 leading-relaxed">
          Today, we've grown into a trusted travel partner for thousands of families, couples, and adventurers across India. From budget backpacking trips to luxury honeymoon packages, we handle every detail so you can focus on making memories. Our mission is simple: to take you beyond the ordinary, beyond the expected — Beyond Horizons.
        </p>
      </div>

      {/* Stats */}
      <div className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-4xl font-bold text-gold mb-2">{s.num}</p>
              <p className="font-body text-sm text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Values */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Our Values</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map(v => (
              <div key={v.title} className="p-6 rounded-3xl bg-navy-50 border border-gray-100 hover:-translate-y-2 transition-transform duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-2xl flex items-center justify-center mb-4 text-gold">{v.icon}</div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{v.title}</h3>
                <p className="font-body text-sm text-gray-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-navy py-16">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-5">Ready to Travel With Us?</h2>
          <p className="font-body text-base text-gray-400 mb-8">Let our experts create the perfect itinerary for your next adventure.</p>
          <Link to="/contact" data-testid="about-cta-btn" className="inline-flex items-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full px-8 py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5">
            Get in Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
